/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Icon } from '@/components/Icon';
import { Button, StandalonePage } from '@/design-system';
import i18n from '@/i18n/i18n';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

/** Top-level error boundary so a render failure never blanks the whole app. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        this.props.fallback ?? (
          <StandalonePage
            title={i18n.t('error.title')}
            icon="error"
            iconTone="danger"
            actions={
              <Button type="button" onClick={() => window.location.reload()}>
                <Icon name="refresh" size={16} />
                {i18n.t('error.reload')}
              </Button>
            }
          >
            {/* A raw message can be a whole stack: it scrolls in its own block rather than
                pushing Reload off the screen. */}
            <pre className="max-h-[30dvh] overflow-auto whitespace-pre-wrap break-words rounded-[var(--radius)] bg-[var(--color-muted)] p-3 font-mono text-xs text-[var(--color-foreground)]">
              {this.state.error.message}
            </pre>
          </StandalonePage>
        )
      );
    }
    return this.props.children;
  }
}
